import React from "react";
import AuthenticityTokenField from "../../../components/forms/AuthenticityTokenField";
import { BillingBanner } from "../../../components/billing/BillingBanner";
import { DashboardPageLayout } from "../../../components/layout/DashboardPageLayout";
import { Alert, AlertDescription } from "../../../components/ui/alert";
import { Button } from "../../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { ssrFormattedDateOr } from "../../../lib/ssrFormattedDate";
import type { BillingSummary, MainAppPageProps } from "../types";

const BILLING_STATUS_ACTIVE = "active";
const BILLING_STATUS_TRIALING = "trialing";
const BILLING_STATUS_PAST_DUE = "past_due";
const BILLING_STATUS_CANCELED = "canceled";
const BILLING_STATUS_UNPAID = "unpaid";
const BILLING_PROVIDER_APP_STORE = "app_store";

type BillingPageProps = Pick<
  MainAppPageProps,
  "csrfToken" | "routes" | "billing" | "billingReturn" | "organization"
>;

type BillingActionHints = {
  checkout: { enabled: boolean; hint: string | null };
  portal: { enabled: boolean; hint: string | null };
};

const PORTAL_DISABLED_HINT =
  "Available after you start checkout once—we create your secure Stripe customer when you add a payment method.";
const APP_STORE_MANAGED_HINT =
  "This subscription is managed through the App Store on iPhone or iPad.";

function hasAnySubscription(
  billing: BillingSummary | null | undefined,
): boolean {
  return Boolean(billing?.hasSubscription ?? billing?.hasStripeSubscription);
}

function isAppStoreProvider(
  billing: BillingSummary | null | undefined,
): boolean {
  return billing?.provider === BILLING_PROVIDER_APP_STORE;
}

function billingActionsFor(
  billing: BillingSummary | null | undefined,
): BillingActionHints {
  if (billing?.billingActions) {
    return billing.billingActions;
  }
  const hasCustomer = Boolean(
    billing?.hasStripeCustomer ?? billing?.hasPaymentMethod,
  );
  const hasSub = hasAnySubscription(billing);
  const appStore = isAppStoreProvider(billing);
  return {
    checkout: {
      enabled:
        !hasSub ||
        billing?.status === BILLING_STATUS_CANCELED ||
        (appStore &&
          billing?.status !== BILLING_STATUS_ACTIVE &&
          billing?.status !== BILLING_STATUS_TRIALING),
      hint:
        appStore &&
        billing?.status !== BILLING_STATUS_CANCELED &&
        billing?.status !== BILLING_STATUS_PAST_DUE &&
        billing?.status !== BILLING_STATUS_UNPAID
          ? APP_STORE_MANAGED_HINT
          : null,
    },
    portal: {
      enabled: appStore ? false : hasCustomer,
      hint: appStore
        ? APP_STORE_MANAGED_HINT
        : hasCustomer
          ? null
          : PORTAL_DISABLED_HINT,
    },
  };
}

function billingStatusLabel(
  billing: BillingSummary,
  organization?: MainAppPageProps["organization"],
): string {
  if (
    organization?.trialExpired &&
    !organization.suitePaid &&
    billing.status !== BILLING_STATUS_ACTIVE &&
    !hasAnySubscription(billing)
  ) {
    return "Trial ended";
  }
  if (billing.restricted && billing.status === BILLING_STATUS_TRIALING) {
    return "Trial ended — payment required";
  }
  if (billing.restricted) {
    return "Restricted";
  }
  if (billing.inFreeTrialWithActiveSubscription) {
    return "Active";
  }
  if (billing.status === BILLING_STATUS_TRIALING) {
    return "Trialing";
  }
  if (billing.status === BILLING_STATUS_ACTIVE) {
    return "Active";
  }
  if (billing.status === BILLING_STATUS_PAST_DUE) {
    return "Past due";
  }
  if (billing.status === BILLING_STATUS_CANCELED) {
    return "Canceled";
  }
  if (billing.status === BILLING_STATUS_UNPAID) {
    return "Unpaid";
  }
  return billing.status || "Unknown";
}

/** Only when the plan grid alone is not enough (suspended, payment issues, cancel scheduled). */
function billingExplainer(
  billing: BillingSummary,
): { headline: string; paragraphs: string[] } | null {
  const periodEndDisplay = ssrFormattedDateOr(
    billing.subscriptionPeriodEndAtDisplay,
  );
  const restricted = Boolean(billing.restricted);

  if (restricted) {
    return {
      headline: "Access is suspended",
      paragraphs: [
        isAppStoreProvider(billing)
          ? "Your public site and quote inbox stay paused until the App Store subscription is active again. Subscribe again on iPhone or iPad to restore access."
          : "Your public site and quote inbox stay paused until billing is back in good standing. Use checkout below to add a payment method and restore access.",
      ],
    };
  }

  if (billing.subscriptionCancelAtPeriodEnd && periodEndDisplay) {
    return {
      headline: "Cancellation scheduled",
      paragraphs: [
        `Your plan stays active through ${periodEndDisplay}. You will not be charged again for this billing period.`,
      ],
    };
  }

  if (billing.status === BILLING_STATUS_PAST_DUE) {
    return {
      headline: "Payment issue",
      paragraphs: [
        isAppStoreProvider(billing)
          ? "We could not renew the App Store subscription for this company. Manage it in the App Store on iPhone or iPad, or start a new web checkout here."
          : "We could not collect a recent payment. Use the billing portal to pay open invoices or update your default payment method.",
      ],
    };
  }

  if (billing.status === BILLING_STATUS_UNPAID) {
    return {
      headline: "Unpaid balance",
      paragraphs: [
        isAppStoreProvider(billing)
          ? "The App Store subscription needs attention before access can continue."
          : "Use the billing portal to resolve invoices or update your payment method.",
      ],
    };
  }

  if (billing.status === BILLING_STATUS_CANCELED) {
    return {
      headline: "Subscription ended",
      paragraphs: [
        isAppStoreProvider(billing)
          ? "Use checkout below if you want to start a new web subscription, or resubscribe in the App Store on iPhone or iPad."
          : "Use checkout below if you want to start billing again.",
      ],
    };
  }

  return null;
}

function billingPageSubtitle(
  billing: BillingSummary | null | undefined,
  organization?: MainAppPageProps["organization"],
): string {
  if (!billing) {
    return "Loading billing…";
  }
  if (billing.restricted) {
    return "Restore payment to bring your website and quote tools back online.";
  }
  const suitePaid =
    Boolean(organization?.suitePaid) ||
    billing.status === BILLING_STATUS_ACTIVE;
  if (organization?.trialExpired && !suitePaid) {
    return "Trial ended. $9/month keeps the suite.";
  }
  if (!suitePaid && organization?.trialEndsAtDisplay) {
    return `Trial through ${organization.trialEndsAtDisplay}. Then $9/month.`;
  }
  return "Then $9/month for the suite.";
}

const BillingPage: React.FC<BillingPageProps> = ({
  csrfToken,
  routes,
  billing,
  billingReturn,
  organization,
}) => {
  const actions = billingActionsFor(billing);
  const explainer = billing ? billingExplainer(billing) : null;
  const showScheduleBlock =
    Boolean(billing?.showTrialEndsInUi) || hasAnySubscription(billing);
  const providerLabel = billing?.providerLabel?.trim() || "Not started";
  const providerIsAppStore = isAppStoreProvider(billing);

  return (
    <DashboardPageLayout
      className="gap-5 pt-3"
      title="Billing"
      subtitle={billingPageSubtitle(billing, organization)}
    >
      <div className="flex w-full flex-col gap-6">
        {billingReturn === "success" && (
          <Alert variant="success">
            <AlertDescription>
              Checkout completed successfully.
            </AlertDescription>
          </Alert>
        )}

        {billingReturn === "canceled" && (
          <Alert variant="default">
            <AlertDescription>
              Checkout was canceled—nothing was charged. You can try again
              anytime.
            </AlertDescription>
          </Alert>
        )}

        <BillingBanner billing={billing} billingRoute={routes.billing} />

        {explainer && (
          <Card className="bg-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">{explainer.headline}</CardTitle>
              <CardDescription className="text-base text-foreground/90">
                {explainer.paragraphs.map((p, i) => (
                  <span key={i} className={i > 0 ? "mt-3 block" : "block"}>
                    {p}
                  </span>
                ))}
              </CardDescription>
            </CardHeader>
          </Card>
        )}

        <Card className="bg-card">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg">Plan details</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-8">
            {showScheduleBlock ? (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Trial & billing
                </p>
                <dl className="mt-4 grid gap-6 sm:grid-cols-2">
                  {billing?.showTrialEndsInUi === true && (
                    <div>
                      <dt className="text-sm font-medium text-muted-foreground">
                        Free trial access ends
                      </dt>
                      <dd className="mt-1 text-base font-semibold text-foreground">
                        {ssrFormattedDateOr(billing?.trialEndsAtDisplay)}
                      </dd>
                    </div>
                  )}
                  <div>
                    <dt className="text-sm font-medium text-muted-foreground">
                      Current billing period ends
                    </dt>
                    <dd className="mt-1 text-base font-semibold text-foreground">
                      {hasAnySubscription(billing)
                        ? ssrFormattedDateOr(
                            billing?.subscriptionPeriodEndAtDisplay,
                          )
                        : "—"}
                    </dd>
                    <dd className="mt-1 text-xs text-muted-foreground">
                      {hasAnySubscription(billing)
                        ? "When your subscription will renew."
                        : "Populates once you have a subscription."}
                    </dd>
                  </div>
                </dl>
              </div>
            ) : null}

            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Plan & account
              </p>
              <dl className="mt-4 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">
                    Plan
                  </dt>
                  <dd className="mt-1 text-base font-semibold text-foreground">
                    ToolKit
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">
                    Price
                  </dt>
                  <dd className="mt-1 text-base font-semibold text-foreground">
                    $9/month full suite
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">
                    Account status
                  </dt>
                  <dd className="mt-1 text-base font-semibold text-foreground">
                    {billing ? billingStatusLabel(billing, organization) : "—"}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">
                    Billing provider
                  </dt>
                  <dd className="mt-1 text-base font-semibold text-foreground">
                    {providerLabel}
                  </dd>
                </div>
              </dl>
            </div>
          </CardContent>
        </Card>

        <div className="grid gap-4 md:grid-cols-2">
          <Card className="bg-card">
            <CardHeader>
              <CardTitle className="text-base">
                Subscribe or reactivate
              </CardTitle>
              <CardDescription>
                Starts or restarts the $9/month full suite (website, job photos,
                and credentials).
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              <form action={routes.billingCheckout} method="post">
                <AuthenticityTokenField csrfToken={csrfToken} />
                <Button
                  type="submit"
                  variant="default"
                  disabled={!actions.checkout.enabled}
                >
                  Add payment & subscribe
                </Button>
              </form>
              {actions.checkout.hint && (
                <p className="text-sm text-muted-foreground">
                  {actions.checkout.hint}
                </p>
              )}
            </CardContent>
          </Card>

          <Card className="bg-card">
            <CardHeader>
              <CardTitle className="text-base">
                {providerIsAppStore
                  ? "App Store subscription"
                  : "Billing portal"}
              </CardTitle>
              <CardDescription>
                {providerIsAppStore
                  ? "This company's active subscription is managed in the App Store on iPhone or iPad."
                  : "Card, invoices, plan changes, and cancellation (hosted by Stripe)."}
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              {providerIsAppStore ? (
                <p className="text-sm text-muted-foreground">
                  Billing changes stay in the App Store for Apple-managed
                  subscriptions.
                </p>
              ) : (
                <form action={routes.billingPortal} method="post">
                  <AuthenticityTokenField csrfToken={csrfToken} />
                  <Button
                    type="submit"
                    variant={actions.portal.enabled ? "default" : "outline"}
                    disabled={!actions.portal.enabled}
                  >
                    Open billing portal
                  </Button>
                </form>
              )}
              {actions.portal.hint && (
                <p className="text-sm text-muted-foreground">
                  {actions.portal.hint}
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardPageLayout>
  );
};

export default BillingPage;
